import React, { ChangeEvent, useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./FastBoundAccountModal.scss";
import OwButton from "../../../../shared/OwButton/OwButton";
import OwTitle from "../../../../shared/OwTitle/OwTitle";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../../shared/OwFnInput/OwFnInput";
import { OwIconButton } from "../../../../shared/OwIconButton/OwIconButton";
import OwSelectOption from "../../../../shared/OwSelectOption/OwSelectOption";
import { ReactHookInput } from "../../../../shared/ReactHookInput/ReactHookInput";
import IFastbound_Account from "../../../../../models/IFastbound_Account";
import FastBoundDispositionTypeEnum from "../../../../../models/FastBoundDispositionTypeEnum";
import FastBoundDispositionRequestTypeEnum from "../../../../../models/FastBoundDispositionRequestTypeEnum";
import FastBoundAcquisitionTypeFilterEnum from "../../../../../models/FastBoundAcquisitionTypeFilterEnum";
import IErrorDTO from "../../../../../models/DTO/IErrorDTO";
import INotification from "../../../../../models/INotification";
import NotificationLevelEnum from "../../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../../models/NotificationPositionEnum";
import UserContext from "../../../../../contexts/UserContext";
import SupplierContext from "../../../../../contexts/SupplierContext";
import FastBoundService from "../../../../../services/FastBoundService";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { OwAccordion } from "../../../../shared/OwAccordion/OwAccordion";
import TitleTagEnum from "../../../../../models/TitleTagEnum";

type Props = {
    closeModalFn: (reloadAccounts?: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    account?: IFastbound_Account;
};

interface ValidatedFields {
    name: string;
    fastbound_api_key: string;
    fastbound_account_number: string;
    fastbound_audit_user_email: string;
}

interface ConfigFormData {
    fastbound_is_enabled: boolean;
    disposition_workflow_step_id: number | null;
    disposition_type: FastBoundDispositionTypeEnum | null;
    disposition_category: FastBoundDispositionRequestTypeEnum | null;
    nfa_disposition_type: FastBoundDispositionTypeEnum | null;
    acquisition_type_filter: FastBoundAcquisitionTypeFilterEnum | null;
    serial_format_pattern: string;
    auto_sync_acquisitions: boolean;
    auto_sync_contacts: boolean;
    disposition_hold_pending: boolean;
}

const createValidationSchema = (isEditMode: boolean) =>
    yup.object().shape({
        name: yup.string().trim().required("Name is required"),
        fastbound_api_key: isEditMode ? yup.string().defined() : yup.string().required("API Key is required"),
        fastbound_account_number: yup.string().trim().required("Account number is required"),
        fastbound_audit_user_email: yup.string().email("Must be a valid email").required("Audit user email is required"),
    });

const dispositionTypeOptions = Object.values(FastBoundDispositionTypeEnum).map((v) => ({ value: v, title: v }));

const dispositionCategoryOptions = Object.values(FastBoundDispositionRequestTypeEnum).map((v) => ({
    value: v,
    title: v === FastBoundDispositionRequestTypeEnum.THEFT_LOSS ? "Theft/Loss" : v,
}));

const acquisitionTypeFilterOptions = Object.values(FastBoundAcquisitionTypeFilterEnum).map((v) => ({
    value: v,
    title: v.charAt(0).toUpperCase() + v.slice(1),
}));

export const FastBoundAccountModal: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const isEditMode = !!props.account;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isDirty: isFormDirty },
    } = useForm<ValidatedFields>({
        resolver: yupResolver(createValidationSchema(isEditMode)) as any,
        defaultValues: {
            name: props.account?.name || "",
            fastbound_api_key: "",
            fastbound_account_number: props.account?.fastbound_account_number || "",
            fastbound_audit_user_email: props.account?.fastbound_audit_user_email || "",
        },
    });

    const initialConfigData: ConfigFormData = {
        fastbound_is_enabled: props.account?.fastbound_is_enabled ?? true,
        disposition_workflow_step_id: props.account?.disposition_workflow_step_id || null,
        disposition_type: props.account?.disposition_type || null,
        disposition_category: props.account?.disposition_category || null,
        nfa_disposition_type: props.account?.nfa_disposition_type || null,
        acquisition_type_filter: props.account?.acquisition_type_filter || null,
        serial_format_pattern: props.account?.serial_format_pattern || "",
        auto_sync_acquisitions: props.account?.auto_sync_acquisitions ?? false,
        auto_sync_contacts: props.account?.auto_sync_contacts ?? false,
        disposition_hold_pending: props.account?.disposition_hold_pending ?? false,
    };
    const initialConfigRef = useRef(initialConfigData);

    const [configData, setConfigData] = useState<ConfigFormData>(initialConfigData);

    const isConfigDirty = isEditMode && JSON.stringify(configData) !== JSON.stringify(initialConfigRef.current);
    const hasUnsavedChanges = isEditMode && (isFormDirty || isConfigDirty);

    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message?: string } | null>(null);
    const [showApiKey, setShowApiKey] = useState(false);

    const handleConfigChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;
        setConfigData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;
        setConfigData((prev) => ({ ...prev, [name!]: value }));
    };

    const handleTestConnection = async () => {
        const apiKey = watch("fastbound_api_key");
        const accountNumber = watch("fastbound_account_number");

        if (!accountNumber) return;

        setIsTesting(true);
        setTestResult(null);

        try {
            if (isEditMode && !apiKey) {
                await FastBoundService.testAccountConnection(props.account!.id, userModel, supplierModel);
                setTestResult({ success: true, message: "Connection successful" });
            } else if (apiKey) {
                await FastBoundService.testConnection(apiKey, accountNumber, userModel, supplierModel);
                setTestResult({ success: true, message: "Connection successful" });
            } else {
                setTestResult({ success: false, message: "API key is required" });
            }
        } catch (err: any) {
            setTestResult({ success: false, message: err.message || "Connection failed" });
        } finally {
            setIsTesting(false);
        }
    };

    const onSubmit = async (data: ValidatedFields) => {
        setIsSaving(true);

        try {
            if (isEditMode) {
                const updateBody: any = {
                    name: data.name,
                    account_number: data.fastbound_account_number,
                    audit_user_email: data.fastbound_audit_user_email,
                    is_enabled: configData.fastbound_is_enabled,
                };
                if (data.fastbound_api_key) {
                    updateBody.api_key = data.fastbound_api_key;
                }
                await FastBoundService.updateAccount(props.account!.id, updateBody, userModel, supplierModel);

                await FastBoundService.updateConfig(
                    {
                        fastbound_account_id: props.account!.id,
                        disposition_workflow_step_id: configData.disposition_workflow_step_id,
                        disposition_type: configData.disposition_type,
                        disposition_category: configData.disposition_category,
                        nfa_disposition_type: configData.nfa_disposition_type,
                        acquisition_type_filter: configData.acquisition_type_filter,
                        serial_format_pattern: configData.serial_format_pattern || null,
                        auto_sync_acquisitions: configData.auto_sync_acquisitions,
                        auto_sync_contacts: configData.auto_sync_contacts,
                        disposition_hold_pending: configData.disposition_hold_pending,
                    },
                    userModel,
                    supplierModel
                );
            } else {
                await FastBoundService.createAccount(
                    {
                        name: data.name,
                        api_key: data.fastbound_api_key,
                        account_number: data.fastbound_account_number,
                        audit_user_email: data.fastbound_audit_user_email,
                        is_enabled: configData.fastbound_is_enabled,
                    },
                    userModel,
                    supplierModel
                );
            }

            props.notifyFn(null, {
                message: isEditMode ? "successfully_updated_account" : "successfully_created_account",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            });

            props.closeModalFn(true);
        } catch (err: any) {
            props.notifyFn(null, {
                message: err.message || "failed_to_save_account",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={"FastBoundAccountModal modal-content"}>
            <div className={"modal-header d-flex justify-content-between align-items-center"}>
                <OwTitle
                    leftIconName={"fad fa-crosshairs"}
                    title={isEditMode ? "edit_fastbound_account" : "add_fastbound_account"}
                    tag={"h3"}
                    defaultMargins={false}
                />
                <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={() => props.closeModalFn()} />
            </div>

            <div className={"modal-body"}>
                <div className="mb-2">
                    <OwLabel title={"name"} />
                    <ReactHookInput
                        register={register("name")}
                        error={errors.name}
                        name={"name"}
                        placeholder={"name"}
                        isRequired={true}
                        autoComplete={"one-time-code"}
                    />
                    {errors.name && <span className={"text-danger small"}>{errors.name.message}</span>}
                </div>

                <div className="mb-2">
                    <OwLabel title={"api_key"} />
                    <div className={"d-flex align-items-center"}>
                        <div className={"flex-grow-1"}>
                            <ReactHookInput
                                register={register("fastbound_api_key")}
                                error={errors.fastbound_api_key}
                                name={"fastbound_api_key"}
                                type={showApiKey ? "text" : "password"}
                                placeholder={isEditMode ? "leave_blank_to_keep_existing" : "api_key"}
                                isRequired={!isEditMode}
                                autoComplete={"new-password"}
                            />
                        </div>
                        <OwIconButton
                            iconClass={showApiKey ? "fad fa-eye-slash" : "fad fa-eye"}
                            iconColorClass={"text-muted ms-2"}
                            fn={() => setShowApiKey((prev) => !prev)}
                        />
                    </div>
                    {errors.fastbound_api_key && <span className={"text-danger small"}>{errors.fastbound_api_key.message}</span>}
                </div>

                <div className="mb-2">
                    <OwLabel title={"account_number"} />
                    <ReactHookInput
                        register={register("fastbound_account_number")}
                        error={errors.fastbound_account_number}
                        name={"fastbound_account_number"}
                        placeholder={"account_number"}
                        isRequired={true}
                    />
                    {errors.fastbound_account_number && (
                        <span className={"text-danger small"}>{errors.fastbound_account_number.message}</span>
                    )}
                </div>

                <div className="mb-2">
                    <OwLabel title={"audit_user_email"} />
                    <ReactHookInput
                        register={register("fastbound_audit_user_email")}
                        error={errors.fastbound_audit_user_email}
                        name={"fastbound_audit_user_email"}
                        type={"email"}
                        placeholder={"audit_user_email"}
                        isRequired={true}
                    />
                    {errors.fastbound_audit_user_email && (
                        <span className={"text-danger small"}>{errors.fastbound_audit_user_email.message}</span>
                    )}
                </div>

                <div className="mb-2">
                    <OwToggle
                        name={"fastbound_is_enabled"}
                        label={"enabled"}
                        active={configData.fastbound_is_enabled}
                        fn={handleCheckboxChange}
                    />
                </div>

                <div className="mb-3 d-flex align-items-center">
                    <OwButton
                        title={"test_connection"}
                        fn={handleTestConnection}
                        active={isTesting}
                        disableOnActive={true}
                        className={"btn-outline-primary"}
                        leftIconClass={"fad fa-plug"}
                    />
                    {testResult && (
                        <span className={`ms-2 badge bg-${testResult.success ? "success" : "danger"}`}>
                            {testResult.success ? "Connected" : testResult.message}
                        </span>
                    )}
                </div>

                {isEditMode && (
                    <>
                        <OwAccordion title={"disposition_settings"} titleTag={TitleTagEnum.H5} fullWidthClick={true} className={"mt-3"}>
                            <div className="p-2">
                                <div className="mb-2">
                                    <OwLabel title={"disposition_workflow_step"} />
                                    <OwSelectOption
                                        options={supplierModel.order_progress_states || []}
                                        valueProp={"order_state_id"}
                                        titleProp={"name"}
                                        name={"disposition_workflow_step_id"}
                                        selectedOption={configData}
                                        selectedOptionProp={"disposition_workflow_step_id"}
                                        fn={handleConfigChange}
                                        showBlankFirst={true}
                                        translateTitle={true}
                                    />
                                </div>

                                <div className="mb-2">
                                    <OwLabel title={"disposition_type"} />
                                    <OwSelectOption
                                        options={dispositionTypeOptions}
                                        valueProp={"value"}
                                        titleProp={"title"}
                                        name={"disposition_type"}
                                        selectedOption={configData}
                                        selectedOptionProp={"disposition_type"}
                                        fn={handleConfigChange}
                                        showBlankFirst={true}
                                    />
                                </div>

                                <div className="mb-2">
                                    <OwLabel title={"disposition_request_type"} />
                                    <OwSelectOption
                                        options={dispositionCategoryOptions}
                                        valueProp={"value"}
                                        titleProp={"title"}
                                        name={"disposition_category"}
                                        selectedOption={configData}
                                        selectedOptionProp={"disposition_category"}
                                        fn={handleConfigChange}
                                        showBlankFirst={true}
                                    />
                                    <span className={"text-muted small d-block"}>
                                        Fallback routing for items without an is_nfa flag. Most accounts should leave this as Regular.
                                    </span>
                                </div>

                                <div className="mb-2">
                                    <OwLabel title={"nfa_disposition_type"} />
                                    <OwSelectOption
                                        options={dispositionTypeOptions}
                                        valueProp={"value"}
                                        titleProp={"title"}
                                        name={"nfa_disposition_type"}
                                        selectedOption={configData}
                                        selectedOptionProp={"nfa_disposition_type"}
                                        fn={handleConfigChange}
                                        showBlankFirst={true}
                                    />
                                    <span className={"text-muted small d-block"}>
                                        Default NFA transfer type when a customer&apos;s Vendor Integration doesn&apos;t specify one.
                                        Typically Form 3 for dealers or Form 4 for civilians.
                                    </span>
                                </div>

                                <div className="mb-2">
                                    <OwToggle
                                        name={"disposition_hold_pending"}
                                        label={"disposition_hold_pending"}
                                        active={configData.disposition_hold_pending}
                                        fn={handleCheckboxChange}
                                        labelOnRight={true}
                                    />
                                </div>
                            </div>
                        </OwAccordion>

                        <OwAccordion title={"sync_settings"} titleTag={TitleTagEnum.H5} fullWidthClick={true} className={"mt-2"}>
                            <div className="p-2">
                                <div className="mb-2">
                                    <OwLabel title={"acquisition_type_filter"} />
                                    <OwSelectOption
                                        options={acquisitionTypeFilterOptions}
                                        valueProp={"value"}
                                        titleProp={"title"}
                                        name={"acquisition_type_filter"}
                                        selectedOption={configData}
                                        selectedOptionProp={"acquisition_type_filter"}
                                        fn={handleConfigChange}
                                        showBlankFirst={true}
                                    />
                                    <span className={"text-muted small d-block"}>
                                        Filter which acquisitions are synced from FastBound. &quot;manufactured&quot; = only items this
                                        account manufactured.
                                    </span>
                                </div>

                                <div className="mb-2">
                                    <OwToggle
                                        name={"auto_sync_acquisitions"}
                                        label={"auto_sync_acquisitions"}
                                        active={configData.auto_sync_acquisitions}
                                        fn={handleCheckboxChange}
                                    />
                                </div>

                                <div className="mb-2">
                                    <OwToggle
                                        name={"auto_sync_contacts"}
                                        label={"auto_sync_contacts"}
                                        active={configData.auto_sync_contacts}
                                        fn={handleCheckboxChange}
                                    />
                                </div>
                            </div>
                        </OwAccordion>

                        <OwAccordion title={"serial_validation"} titleTag={TitleTagEnum.H5} fullWidthClick={true} className={"mt-2"}>
                            <div className="p-2">
                                <div className="mb-2">
                                    <OwLabel title={"serial_format_pattern"} />
                                    <OwFnInput
                                        fn={handleConfigChange}
                                        name={"serial_format_pattern"}
                                        value={configData.serial_format_pattern}
                                        placeholder={"e.g. ^OC-\\d{4}-\\d{5}$"}
                                    />
                                    <span className={"text-muted small d-block"}>
                                        Regex pattern to validate serial number format during import. Leave blank for no validation.
                                    </span>
                                </div>
                            </div>
                        </OwAccordion>
                    </>
                )}
            </div>

            <div className={"modal-footer d-flex justify-content-between align-items-center"}>
                {hasUnsavedChanges ? <span className={"text-danger small"}>You have unsaved changes</span> : <span />}
                <OwButton title={"save"} fn={handleSubmit(onSubmit)} disableOnActive={true} active={isSaving} />
            </div>
        </div>
    );
};
